/**
 * v0.6.59 — 日期选择器公用 helper (辉哥 9/30 19:42 拍板)
 *
 * 抽出 CalendarTab.initDatePicker + SalesTab.initMonthPicker 重复逻辑,统一封装 3 种 mode:
 *   - 单日期 mode='date' (默认): dateFormat 'Y/m/d' ISO, locale zh fallback, disableMobile UA 检测
 *   - 月份 mode='month': dateFormat 'Y-m' + monthSelectPlugin, disableMobile true (v0.6.18a F2 修复)
 *   - 日期范围 mode='range': dateFormat 'Y/m/d', start/end 两个日期 (flatpickr mode='range' 原生)
 *
 * 沉淀 SOP 105/106/107/108 (跨项目永久):
 *   - SOP 105: 桌面 disableMobile (UA 检测) 走 popover, mobile 走 native picker
 *   - SOP 106: appendTo: document.body 让 popover 脱离 stacking context (配合 LESS z-index:9999)
 *   - SOP 107: LESS input width >= 348px + .flatpickr-calendar width 348px + JS onReady Math.max(inputWidth, 348) 兜底
 *     (v0.7.9i: 容器从 335 加宽到 348, 兜底宽度同步提到 348)
 *   - SOP 108: "同款"任务必须 puppeteer 实测 visual 对比 (本 helper 三个 mode 已对齐 BookingModal 视觉)
 *
 * vendor: window.flatpickr + window.monthSelectPlugin 由 ziven-scheduler/js/src/forum/index.js 异步加载.
 * helper 内部 await waitForVendor() 等 vendor 就绪 (最多 5s), vendor 加载失败返 null 让 caller 自己 fallback.
 *
 * @param {HTMLElement} inputEl - 触发日期选择器的 text input (必填, DOM 已存在)
 * @param {object} [opts] - 用户配置
 * @param {'date'|'month'|'range'} [opts.mode='date'] - 选择器模式
 * @param {Date|string|string[]} [opts.defaultDate] - 默认日期 (按 mode 解释不同)
 * @param {string} [opts.dateFormat] - dateFormat (按 mode 已有默认值, 也可覆盖)
 * @param {Function} [opts.onChange] - (selectedDates, dateStr, instance) => void
 * @param {Function} [opts.onReady] - (selectedDates, dateStr, instance) => void
 * @param {Function} [opts.onClose] - (selectedDates, dateStr, instance) => void
 * @param {boolean} [opts.disableMobile] - **v0.18.35 新增, 默认 = !isMobileUA (即老行为)**。
 *   传 true ⇒ 强制走 flatpickr 自己的 popover, **不**克隆 native `<input type="date">`。
 *   ⚠️ 为什么需要这个开关 (而不是各 caller 自己判): flatpickr 的 `isMobile` 分支会
 *   **自己克隆一个 native input** (`w.mobileInput = d("input", w.input.className + " flatpickr-mobile")`),
 *   原 input 与克隆体同时在 DOM 里 ⇒ 窄屏上要么「两个一模一样的日期框」(其中一个是死的),
 *   要么得靠 CSS `:has(.flatpickr-mobile)` 把老 input 藏掉 (见 forum.less v0.7.9f 段)。
 *   本仓 modal 内的日期框一律要**单一可见控件 + 自己控制显示文案**, 所以传 true。
 *   ⚠️ 不传 ⇒ 行为与改动前**逐字相同** (CalendarTab / SalesTab / StatsTab / OperationLogsTab 零回归)。
 * @returns {Promise<object|null>} flatpickr instance (含 destroy/setDate 等方法), vendor 未就绪返 null
 */
export async function mountDatePicker(inputEl, opts) {
  if (opts === void 0) {
    opts = {};
  }
  if (!inputEl) return null;
  const mode = opts.mode || 'date';

  // vendor async 加载等待 (flatpickr + zh locale + monthSelect plugin 可能顺序加载)
  await waitForVendor(mode);
  if (typeof window.flatpickr === 'undefined' || typeof window.flatpickr.l10ns === 'undefined') {
    console.warn('[ziven-scheduler] DatePickerHelper: window.flatpickr 未加载, mode=' + mode + ' 初始化失败');
    return null;
  }

  // 移动端 UA 检测 (跟 v0.6.12b CalendarTab 一致, iPhone/iPad/iPod/Android 命中走 native picker)
  const isMobileUA = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // 通用 config (SOP 105/106/107 兜底, 三个 mode 都共享)
  //   钩子列表统一遍历, 只在 opts 有值时注入, 避免 Object.assign 覆盖 vendor defaults 的 array []
  //   (v0.7.2 修: 之前显式 `onChange: opts.onChange` 等, opts undefined 时 Object.assign 会把 vendor
  //   defaults 的 array [] 覆盖成 undefined, monthSelect plugin L284 `fp.config.onClose.push(closeHook)`
  //   crash — SalesTab 报错根因)
  //   默认 onReady always 注入 (内联 SOP 107/108 兜底), 其他 hook 只在 opts 有值时注入
  //   未来扩展 (onMonthChange / onYearChange / onDayCreate 等) 只需往 HOOK_KEYS 数组加一项, 不会漏
  const HOOK_KEYS = ['onChange', 'onClose', 'onReady', 'onOpen', 'onDestroy', 'onValueUpdate', 'onKeyDown', 'onMonthChange', 'onYearChange', 'onDayCreate', 'onParseConfig'];
  const baseConfig = {
    locale: window.flatpickr.l10ns && window.flatpickr.l10ns.zh ? window.flatpickr.l10ns.zh : 'default',
    appendTo: document.body,
    onReady: (selectedDates, dateStr, instance) => {
      // SOP 107 兜底: popover 宽度 = max(inputWidth, 348), 防止 LESS 失效时 weekday 列被裁剪
      //   v0.7.9i 容器从 335 加宽到 348 (A2 连体分段设计), 兜底宽度同步提到 348
      if (instance && instance.calendarContainer && inputEl) {
        const inputWidth = inputEl.getBoundingClientRect().width;
        if (inputWidth > 0) {
          const popupWidth = Math.max(inputWidth, 348);
          instance.calendarContainer.style.width = popupWidth + 'px';
        }
      }
      // v0.6.59b — 试图在 mobile 端隐藏原 flatpickr text input (Bug #2)
      //   ⚠️ v0.7.9f 实测: **这行不生效, 保留仅为兼容, 真正的修法在 LESS**。
      //   原因: mithril 的 updateAttr 拿活的 DOM 属性做比较 (不是上次 vnode 值),
      //   vnode 里写死 type="text" → flatpickr 改成 "hidden" → mithril redraw 时写回 "text"。
      //   而 CalendarTab 每次切日期都 load() → m.redraw(), 也就是改一次被还原一次。
      //   现在改成 LESS 用 :has() 隐藏 (见 less/forum.less v0.7.9f 段, 有完整根因注释)。
      //   这里保留本行: ① 对非 mithril 挂载的 input 仍有效 ② 保持 flatpickr 内部状态一致
      if (instance && instance._input && (mode === 'date' || mode === 'range') && instance.isMobile) {
        instance._input.type = 'hidden';
      }
      // ============================================================
      // v0.7.9i — 桌面端「今天」按钮 + 作用域 class (辉哥 10/5 拍板, A2 连体分段)
      //
      // 只在桌面 + mode === 'date' 时生效 (range/month/mobile native picker 都不挂)
      // 作用域: 给 calendarContainer 加 .ZS-fp, 让 LESS 段的覆盖只作用于本扩展
      //   (避免污染 ziven-bjxy-website 的 BookingModal 用的 ziven-core flatpickr)
      // ============================================================
      if (instance && instance.calendarContainer && mode === 'date' && !instance.isMobile) {
        instance.calendarContainer.classList.add('ZS-fp');
        const monthsEl = instance.calendarContainer.querySelector('.flatpickr-months');
        if (monthsEl && !monthsEl.querySelector('.ZS-fp-today')) {
          const todayBtn = document.createElement('button');
          todayBtn.type = 'button';
          todayBtn.className = 'ZS-fp-today';
          todayBtn.textContent = '今天';
          todayBtn.addEventListener('click', e => {
            e.preventDefault();
            e.stopPropagation();
            instance.setDate(new Date(), true);
            instance.close();
          });
          monthsEl.appendChild(todayBtn);
        }
      }
      if (opts.onReady) opts.onReady(selectedDates, dateStr, instance);
    }
  };
  HOOK_KEYS.forEach(k => {
    if (k === 'onReady') return; // 默认 onReady 已注入 (上方内联函数), 不重复
    if (opts[k]) baseConfig[k] = opts[k];
  });

  // mode-specific config
  let config;
  if (mode === 'month') {
    // 跟 SalesTab v0.6.19 同款, disableMobile true (v0.6.18a F2 修复: 月份 popover 不出 native picker)
    if (typeof window.monthSelectPlugin === 'undefined') {
      console.warn('[ziven-scheduler] DatePickerHelper: monthSelect plugin 未加载, mode=month 初始化失败');
      return null;
    }
    config = {
      ...baseConfig,
      plugins: [new window.monthSelectPlugin({
        shorthand: false,
        dateFormat: opts.dateFormat || 'Y-m'
      })],
      dateFormat: opts.dateFormat || 'Y-m',
      defaultDate: opts.defaultDate,
      enableTime: false,
      disableMobile: true
    };
  } else if (mode === 'range') {
    // 日期范围 (flatpickr 原生 mode='range', 一个 input 框 + popover 选 start~end)
    config = {
      ...baseConfig,
      dateFormat: opts.dateFormat || 'Y/m/d',
      mode: 'range',
      defaultDate: opts.defaultDate,
      enableTime: false,
      disableMobile: !isMobileUA
    };
  } else {
    // mode='date' (跟 CalendarTab v0.6.12b 同款, UA 检测决定 popover vs native picker)
    //   v0.18.35 — disableMobile 改成**可选**: 默认 (!isMobileUA) = 老行为, 零回归;
    //   传 true 的 caller (约课弹窗的日期框) 强制走 popover, 避免 flatpickr 克隆 native input
    //   造成「两个日期框」/ 显示文案失控 (理由见 JSDoc 的 opts.disableMobile)。
    config = {
      ...baseConfig,
      dateFormat: opts.dateFormat || 'Y/m/d',
      defaultDate: opts.defaultDate,
      enableTime: false,
      disableMobile: opts.disableMobile != null ? opts.disableMobile : !isMobileUA
    };
  }
  return window.flatpickr(inputEl, config);
}

// vendor flatpickr / monthSelectPlugin 异步加载等待
//   ziven-scheduler/js/src/forum/index.js 用 XHR 同步 fetch 加载, 一般在 app.initializers 触发前完成,
//   但 tab 切换时 input DOM 存在 vendor 未必就绪, 这里 poll 等到就绪 (上限 5s)
async function waitForVendor(mode) {
  let retries = 0;
  const maxRetries = 50; // 50 * 100ms = 5s
  return new Promise(resolve => {
    const tick = () => {
      const fpReady = typeof window.flatpickr !== 'undefined' && typeof window.flatpickr.l10ns !== 'undefined';
      const monthPluginReady = mode !== 'month' || typeof window.monthSelectPlugin !== 'undefined';
      if (fpReady && monthPluginReady) return resolve();
      if (retries >= maxRetries) return resolve();
      retries++;
      setTimeout(tick, 100);
    };
    tick();
  });
}